import React from 'react';
import {
  View,
  type ViewProps,
  type TextProps,
  HStack,
  type StackProps,
  Tag,
  type TagProps,
  CMSDefaultIcon,
  ComponentFill24Icon,
  ImageIcon,
  ButtonPrefixSuffix,
  FigmaIcon,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';

const SystemMessageView = emotionStyled(View)({
  userSelect: 'text',
  display: 'flex',
  flexDirection: 'column',
  fontSize: '11.5px',
  '& p': {
    lineHeight: '1.2rem',
  },
  '& ul li ul': {
    marginTop: '0.5em',
  },
  '& [data-token-type]': {
    height: '1em!important',
    '&:not([data-token-type="colorValue"])': {
      '& [data-sc="Icon"]': {
        height: 'calc(1em - 4px) !important',
        position: 'relative',
        '& svg': {
          position: 'absolute',
          height: '1rem !important',
          top: '-4px',
        },
      },
    },
  },
});

// A selection that starts in the bubble but ends past it (a triple-click, or a
// drag past the bubble's edge) would otherwise carry the next reply's text
// into the clipboard, so a copy is clipped to the bubble it started in.
const copyWithinBubble = (event: React.ClipboardEvent<HTMLElement>) => {
  const bubble = event.currentTarget;
  const selection = bubble.ownerDocument.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
  const range = selection.getRangeAt(0);
  if (
    bubble.contains(range.startContainer) &&
    bubble.contains(range.endContainer)
  ) {
    return;
  }
  const clipped = range.cloneRange();
  if (!bubble.contains(clipped.startContainer)) {
    clipped.setStart(bubble, 0);
  }
  if (!bubble.contains(clipped.endContainer)) {
    clipped.setEnd(bubble, bubble.childNodes.length);
  }
  const text = clipped.toString();
  if (!text) return;
  event.clipboardData.setData('text/plain', text);
  event.preventDefault();
  event.stopPropagation();
};

/**
 * ## UserMessageWrapper
 * @extends ViewProps Chat Message Wrapper Element to hold USER message contents
 * @param isFullWidth Fills the message column instead of shrinking to the
 * content. Freeform text reads better shrink-wrapped, so that is the default;
 * a structured message whose height changes as the reader opens it wants a
 * width that does not move with it.
 * @example
 * ```jsx
 *  <UserMessageWrapper>
 *    <MessageContent>This is an user message</MessageContent>
 *  </UserMessageWrapper>
 * ```
 */
export const UserMessageWrapper = ({
  children,
  isFullWidth = false,
  ...props
}: ViewProps & {isFullWidth?: boolean}) => {
  const wrapperStyle = {
    fontSize: '11.5px',
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: '$4',
    marginBottom: '$2',
    marginTop: '$2',
  };
  const boxStyle = {
    lineHeight: '1rem',
    borderRadius: '$2',
    padding: '$2',
    background: '$backgroundRaised',
    // `props` lands on the outer `View`, so a consumer cannot reach this box
    // from the outside, and `width: 100%` on its child resolves circularly
    // against a shrink-to-fit parent. Hence the flag rather than a style prop.
    width: isFullWidth ? '100%' : 'fit-content',
    maxWidth: '100%',
  };
  return (
    <View {...wrapperStyle} {...props}>
      <View userSelect="text" onCopy={copyWithinBubble} {...boxStyle}>
        {children}
      </View>
    </View>
  );
};

/**
 * ## SystemMessageWrapper
 * @extends ViewProps Chat Message Wrapper Element to hold agent message contents
 * @example
 * ```jsx
 *  <SystemMessageWrapper>
 *    <MessageContent>This is a message from Agent</MessageContent>
 *  </SystemMessageWrapper>
 * ```
 */
export const SystemMessageWrapper = ({children, ...props}: ViewProps) => (
  <SystemMessageView {...props}>{children}</SystemMessageView>
);

/**
 * ## MessageContent
 * @extends TextProps Basic Text Content Component for textual messages
 */
export const MessageContent = ({children, ...props}: Partial<TextProps>) => {
  return (
    <View font="body" wordBreak="break-word" overflowWrap="anywhere" {...props}>
      {children}
    </View>
  );
};

/**
 * ## MessageAttachmentList
 * @extends HStack Lists Tags for message attachments and other use cases
 */
export const MessageAttachmentList = ({
  children,
  ...props
}: Partial<StackProps>) => {
  return (
    <HStack marginBottom="$1" shouldWrap maxWidth="100%" {...props}>
      {children}
    </HStack>
  );
};

interface MessageAttachmentProps extends Partial<TagProps> {
  type: 'image' | 'collection' | 'component' | 'figma' | 'custom';
}

type AttachmentMap<Value> = {
  [prop: string | MessageAttachmentProps['type']]: Value;
};

const typeExtendedStyle: AttachmentMap<Partial<TagProps>> = {
  image: {
    colorVariant: 'primary',
  },
};

const typeIcons: AttachmentMap<ButtonPrefixSuffix> = {
  collection: <CMSDefaultIcon />,
  component: <ComponentFill24Icon size={16} />,
  image: <ImageIcon />,
  figma: <FigmaIcon />,
};

/**
 * ## MessageAttachment
 * @extends Tag Holds corresponding icon and style for given type
 */
export const MessageAttachment = ({
  children,
  type,
  ...props
}: MessageAttachmentProps) => {
  const icon = typeIcons[type] ?? null;
  const extendedStyles = typeExtendedStyle[type] ?? {};
  return (
    <Tag
      maxWidth="calc(100% - 16px)"
      density="compact"
      prefix={icon}
      {...extendedStyles}
      {...props}
    >
      {children}
    </Tag>
  );
};
